import { useRef, useImperativeHandle, forwardRef } from 'react'
import Editor from '@monaco-editor/react'
import type * as Monaco from 'monaco-editor'

// ─── Lightweight HTML + Liquid formatter ──────────────────────────────────────

function formatHtml(html: string): string {
  const INDENT = '  '
  const VOID   = new Set(['area','base','br','col','embed','hr','img','input','link','meta','param','source','track','wbr'])
  const INLINE = new Set(['a','abbr','b','bdo','big','br','button','cite','code','dfn','em','i','img','input','kbd','label','map','output','q','samp','select','small','span','strong','sub','sup','textarea','time','tt','u','var'])
  const BLOCK_RAW = ['style','script','pre','textarea']

  // Step 1: lift out raw blocks (style/script/pre) so their { } aren't touched
  const rawBlocks: string[] = []
  let work = html
  for (const tag of BLOCK_RAW) {
    work = work.replace(new RegExp(`(<${tag}[^>]*>)[\\s\\S]*?(<\\/${tag}>)`, 'gi'), (match) => {
      rawBlocks.push(match)
      return `\x00RAW${rawBlocks.length - 1}\x00`
    })
  }

  // Step 2: tokenise — { is now safe to include in plain-text tokens
  const tokens = work.match(/({%[\s\S]*?%}|{{[\s\S]*?}}|{#[\s\S]*?#}|<[^>]+>|[^<]+)/g) ?? []

  let result = ''
  let depth  = 0
  const ind  = () => INDENT.repeat(Math.max(0, depth))

  for (const token of tokens) {
    const trimmed = token.trim()
    if (!trimmed) continue

    // Restore raw block verbatim
    if (/^\x00RAW\d+\x00$/.test(trimmed)) {
      const idx = parseInt(trimmed.replace(/\x00RAW(\d+)\x00/, '$1'), 10)
      result += `\n${ind()}${rawBlocks[idx]}`
      continue
    }

    // Liquid block tag
    if (trimmed.startsWith('{%')) {
      const inner   = trimmed.replace(/^{%-?\s*/, '').replace(/\s*-?%}$/, '')
      const word    = inner.split(/\s+/)[0]
      const isClose = /^end/.test(word) || word === 'else' || word === 'elsif' || word === 'when'
      const isOpen  = !isClose && !['break','continue','assign','increment','decrement','cycle','include','render'].includes(word)
      if (isClose) depth = Math.max(0, depth - 1)
      result += `\n${ind()}${trimmed}`
      if (isOpen) depth++
      continue
    }

    // Liquid output / comment — inline
    if (trimmed.startsWith('{{') || trimmed.startsWith('{#')) {
      result += trimmed
      continue
    }

    // Closing HTML tag
    const closeMatch = trimmed.match(/^<\/(\w+)/)
    if (closeMatch) {
      const tag = closeMatch[1].toLowerCase()
      if (!INLINE.has(tag)) { depth = Math.max(0, depth - 1); result += `\n${ind()}${trimmed}` }
      else result += trimmed
      continue
    }

    // Opening HTML tag
    const openMatch = trimmed.match(/^<(\w+)/)
    if (openMatch) {
      const tag       = openMatch[1].toLowerCase()
      const selfClose = trimmed.endsWith('/>') || VOID.has(tag)
      if (tag.startsWith('!')) { result += `\n${ind()}${trimmed}`; continue }
      if (!INLINE.has(tag)) result += `\n${ind()}${trimmed}`
      else result += trimmed
      if (!selfClose) depth++
      continue
    }

    // Plain text — keep as-is (may contain CSS { } braces)
    const text = token.replace(/[ \t]*\n[ \t]*/g, '\n').trimEnd()
    if (text.trim()) result += text
  }

  return result.replace(/^\n/, '').replace(/\n{3,}/g, '\n\n')
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface LiquidEditorHandle {
  insertText: (text: string) => void
}

interface Props {
  value: string
  onChange: (value: string) => void
  height?: string
}

export const LiquidEditor = forwardRef<LiquidEditorHandle, Props>(
  ({ value, onChange, height = '100%' }, ref) => {
    const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null)

    useImperativeHandle(ref, () => ({
      insertText(text: string) {
        const editor = editorRef.current
        if (!editor) return
        const selection = editor.getSelection()
        if (!selection) return
        editor.executeEdits('drag-insert', [{ range: selection, text, forceMoveMarkers: true }])
        editor.focus()
      },
    }))

    function handleMount(editor: Monaco.editor.IStandaloneCodeEditor, monaco: typeof Monaco) {
      editorRef.current = editor

      monaco.languages.register({ id: 'liquid-html' })

      monaco.languages.setMonarchTokensProvider('liquid-html', {
        defaultToken: '',
        tokenPostfix: '.html',
        tokenizer: {
          root: [
            [/\{#/, 'comment.liquid', '@liquidComment'],
            [/\{%[-~]?/, 'delimiter.liquid', '@liquidBlock'],
            [/\{\{[-~]?/, 'delimiter.liquid', '@liquidOutput'],
            [/<(!DOCTYPE)/, 'metatag.html', '@doctype'],
            [/<!--/, 'comment.html', '@htmlComment'],
            [/(<)(\w+)/, ['delimiter.html', 'tag.html'], '@htmlTag'],
            [/(<\/)(\w+)/, ['delimiter.html', 'tag.html'], '@htmlEndTag'],
            [/<\/>/, 'delimiter.html'],
            [/[^<{]+/, 'text.html'],
          ],
          liquidComment: [
            [/#\}/, 'comment.liquid', '@pop'],
            [/./, 'comment.liquid'],
          ],
          liquidBlock: [
            [/[-~]?%\}/, 'delimiter.liquid', '@pop'],
            [/\b(if|elsif|else|endif|for|endfor|unless|endunless|case|when|endcase|assign|capture|endcapture|increment|decrement|include|render|break|continue|cycle|comment|endcomment|raw|endraw)\b/, 'keyword.liquid'],
            [/\b(in|and|or|not|contains|with)\b/, 'keyword.operator.liquid'],
            [/"([^"]*)"/, 'string.liquid'],
            [/'([^']*)'/, 'string.liquid'],
            [/\b\d+(\.\d+)?\b/, 'number.liquid'],
            [/\b(true|false|nil|null|blank|empty|forloop)\b/, 'constant.liquid'],
            [/[a-zA-Z_][\w.]*/, 'variable.liquid'],
            [/[|:]/, 'delimiter.liquid'],
            [/\s+/, ''],
          ],
          liquidOutput: [
            [/[-~]?\}\}/, 'delimiter.liquid', '@pop'],
            [/"([^"]*)"/, 'string.liquid'],
            [/'([^']*)'/, 'string.liquid'],
            [/\b\d+(\.\d+)?\b/, 'number.liquid'],
            [/\b(true|false|nil|null|blank|empty)\b/, 'constant.liquid'],
            [/\bcollection\b/, 'variable.collection.liquid'],
            [/[a-zA-Z_][\w.]*/, 'variable.liquid'],
            [/[|:]/, 'delimiter.liquid'],
            [/\s+/, ''],
          ],
          doctype: [
            [/>/, 'metatag.html', '@pop'],
            [/[^>]+/, 'metatag.content.html'],
          ],
          htmlComment: [
            [/-->/, 'comment.html', '@pop'],
            [/./, 'comment.html'],
          ],
          htmlTag: [
            [/>/, 'delimiter.html', '@pop'],
            [/\/?>/, 'delimiter.html', '@pop'],
            [/"([^"]*)"/, 'attribute.value.html'],
            [/'([^']*)'/, 'attribute.value.html'],
            [/[\w-]+/, 'attribute.name.html'],
            [/=/, 'delimiter.html'],
            [/\s+/, ''],
          ],
          htmlEndTag: [
            [/>/, 'delimiter.html', '@pop'],
            [/\s+/, ''],
          ],
        },
      })

      monaco.editor.defineTheme('report-studio-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'delimiter.liquid',          foreground: '7c5cfc', fontStyle: 'bold' },
          { token: 'keyword.liquid',             foreground: '4f8ef7', fontStyle: 'bold' },
          { token: 'keyword.operator.liquid',    foreground: '4f8ef7' },
          { token: 'variable.liquid',            foreground: 'e2e8f8' },
          { token: 'variable.collection.liquid', foreground: '3ecf8e', fontStyle: 'bold' },
          { token: 'string.liquid',              foreground: 'f7c948' },
          { token: 'number.liquid',              foreground: 'f7c948' },
          { token: 'constant.liquid',            foreground: 'f75f5f' },
          { token: 'comment.liquid',             foreground: '5a6480', fontStyle: 'italic' },
          { token: 'tag.html',                   foreground: '4f8ef7' },
          { token: 'attribute.name.html',        foreground: '3ecf8e' },
          { token: 'attribute.value.html',       foreground: 'f7c948' },
          { token: 'delimiter.html',             foreground: '5a6480' },
          { token: 'comment.html',               foreground: '5a6480', fontStyle: 'italic' },
          { token: 'metatag.html',               foreground: '5a6480' },
          { token: 'text.html',                  foreground: 'e2e8f8' },
        ],
        colors: {
          'editor.background':               '#0d0f14',
          'editor.foreground':               '#e2e8f8',
          'editor.lineHighlightBackground':  '#13161d',
          'editorLineNumber.foreground':     '#2a3147',
          'editorLineNumber.activeForeground': '#5a6480',
          'editor.selectionBackground':      '#4f8ef730',
          'editorCursor.foreground':         '#4f8ef7',
          'editorIndentGuide.background1':   '#1f2433',
          'editorBracketMatch.background':   '#4f8ef720',
          'editorBracketMatch.border':       '#4f8ef7',
        },
      })

      monaco.editor.setTheme('report-studio-dark')

      // Register formatter for liquid-html using our custom formatHtml
      monaco.languages.registerDocumentFormattingEditProvider('liquid-html', {
        provideDocumentFormattingEdits(model) {
          return [{
            range: model.getFullModelRange(),
            text: formatHtml(model.getValue()),
          }]
        },
      })

      // Auto-format on load
      setTimeout(() => {
        editor.getAction('editor.action.formatDocument')?.run()
      }, 300)
    }

    return (
      <Editor
        height={height}
        language="liquid-html"
        theme="vs-dark"
        value={value}
        onChange={v => onChange(v ?? '')}
        onMount={handleMount}
        loading={
          <div className="flex h-full items-center justify-center bg-bg text-xs text-muted font-mono">
            Loading editor…
          </div>
        }
        options={{
          fontSize: 12,
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 20,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          tabSize: 2,
          insertSpaces: true,
          autoClosingBrackets: 'always',
          autoClosingQuotes: 'always',
          renderLineHighlight: 'line',
          smoothScrolling: true,
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: 'on',
          padding: { top: 12, bottom: 12 },
          scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
        }}
      />
    )
  }
)

LiquidEditor.displayName = 'LiquidEditor'